import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { canStaffSection } from '../utils/staffAccess';
import { useTenantSafe } from '../context/TenantContext';
import {
  getLandValues,
  submitLandValue,
  verifyLandValue,
  getStats,
} from '../services/landService';
import LandValueMap, { CATEGORY_COLOR, CATEGORY_LABEL, CATEGORY_ICON } from '../components/land/LandValueMap';
import LandValueForm, { CATEGORIES } from '../components/land/LandValueForm';

// ─── Status badge ─────────────────────────────────────────────────────────────
const STATUS_COLOR = { draft: '#94a3b8', verified: '#22c55e', disputed: '#f59e0b', archived: '#475569' };

function StatusBadge({ status }) {
  const color = STATUS_COLOR[status] || '#94a3b8';
  return (
    <span style={{
      background: `${color}22`, color, border: `1px solid ${color}55`,
      borderRadius: 20, padding: '2px 9px', fontSize: 11, fontWeight: 600,
    }}>
      {status}
    </span>
  );
}

// ─── Value row card ───────────────────────────────────────────────────────────
function ValueCard({ value, isManager, onVerify, isSelected, onClick }) {
  const color = CATEGORY_COLOR[value.category] || '#94a3b8';
  const icon  = CATEGORY_ICON[value.category]  || '📍';
  const label = CATEGORY_LABEL[value.category] || value.category;

  return (
    <div
      onClick={onClick}
      style={{
        background: isSelected ? '#1e3a5f' : '#1e293b',
        border: `1px solid ${isSelected ? color : '#334155'}`,
        borderLeft: `4px solid ${color}`,
        borderRadius: 8,
        padding: '12px 14px',
        marginBottom: 8,
        cursor: 'pointer',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: '#f1f5f9', fontWeight: 600, fontSize: 14, marginBottom: 4 }}>
            {icon} {value.name}
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 4 }}>
            <span style={{ fontSize: 11, color }}>
              {label}
            </span>
            <StatusBadge status={value.status} />
            <span style={{ fontSize: 11, color: '#64748b' }}>{value.visibility}</span>
          </div>
          {value.description && (
            <p style={{ fontSize: 12, color: '#94a3b8', margin: '4px 0 0',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {value.description}
            </p>
          )}
        </div>
      </div>

      {isSelected && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #334155' }}>
          {value.significance && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 10, color: '#64748b', marginBottom: 3, fontWeight: 700 }}>SIGNIFICANCE</div>
              <p style={{ fontSize: 12, color: '#cbd5e1', margin: 0 }}>{value.significance}</p>
            </div>
          )}
          {value.evidenceNotes && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 10, color: '#64748b', marginBottom: 3, fontWeight: 700 }}>EVIDENCE NOTES</div>
              <p style={{ fontSize: 12, color: '#94a3b8', margin: 0 }}>{value.evidenceNotes}</p>
            </div>
          )}
          {value.tags?.length > 0 && (
            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 8 }}>
              {value.tags.map(t => (
                <span key={t} style={{ fontSize: 10, color: '#475569' }}>#{t}</span>
              ))}
            </div>
          )}
          <div style={{ fontSize: 11, color: '#475569' }}>
            Geometry: {value.geometry?.type}
            {value.createdBy?.name && ` · Submitted by ${value.createdBy.name}`}
          </div>

          {isManager && value.status === 'draft' && (
            <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
              <button
                onClick={(e) => { e.stopPropagation(); onVerify(value._id, 'verified'); }}
                style={{ background: '#16a34a', border: 'none', color: '#fff',
                  borderRadius: 6, padding: '5px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}
              >
                ✓ Verify
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onVerify(value._id, 'disputed'); }}
                style={{ background: 'transparent', border: '1px solid #f59e0b', color: '#f59e0b',
                  borderRadius: 6, padding: '5px 12px', fontSize: 12, cursor: 'pointer' }}
              >
                ⚠ Dispute
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Stats bar ────────────────────────────────────────────────────────────────
function StatsBar({ stats }) {
  if (!stats) return null;
  const { byCategory, byStatus } = stats;
  const total = byStatus?.reduce((a, s) => a + s.count, 0) || 0;

  return (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
      <div style={{ background: '#1e293b', borderRadius: 8, padding: '10px 16px', flex: 1, minWidth: 100 }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: '#f1f5f9' }}>{total}</div>
        <div style={{ fontSize: 11, color: '#64748b' }}>Total values</div>
      </div>
      {byCategory?.slice(0, 4).map(s => (
        <div key={s._id} style={{
          background: '#1e293b', borderRadius: 8, padding: '10px 16px',
          borderTop: `3px solid ${CATEGORY_COLOR[s._id] || '#94a3b8'}`, flex: 1, minWidth: 100,
        }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#f1f5f9' }}>{s.count}</div>
          <div style={{ fontSize: 11, color: '#64748b' }}>
            {CATEGORY_ICON[s._id]} {CATEGORY_LABEL[s._id] || s._id}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function LandValuesPage() {
  const { activeTenant, user } = useAuth();
  const tenantCtx = useTenantSafe();
  const tenantId = tenantCtx?.viewTenant?._id || activeTenant?._id;
  const isManager = canStaffSection(user, tenantId, 'landValues');

  const [values,      setValues]      = useState([]);
  const [stats,       setStats]       = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [tab,         setTab]         = useState('map');     // 'map' | 'list' | 'submit'
  const [catFilter,   setCatFilter]   = useState('');
  const [selectedId,  setSelectedId]  = useState(null);
  const [showManager, setShowManager] = useState(false);

  const load = async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const params = {};
      if (catFilter) params.category = catFilter;
      const [vRes, sRes] = await Promise.all([
        getLandValues(tenantId, params),
        getStats(tenantId),
      ]);
      setValues(vRes.data);
      setStats(sRes.data);
    } catch { /* ignore */ } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [tenantId, catFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (data) => {
    await submitLandValue(tenantId, data);
    await load();
    setTab('list');
  };

  const handleVerify = async (id, status) => {
    try {
      await verifyLandValue(tenantId, id, { status });
      setValues(prev => prev.map(v => v._id === id ? { ...v, status } : v));
    } catch { /* ignore */ }
  };

  if (!tenantId) {
    return (
      <div style={{ padding: 32, color: '#64748b', textAlign: 'center' }}>
        No active community selected.
      </div>
    );
  }

  const tabBtn = (key, label) => (
    <button onClick={() => setTab(key)} style={{
      background: tab === key ? '#164e63' : '#1e293b',
      border: `1px solid ${tab === key ? '#0ea5e9' : '#334155'}`,
      color: tab === key ? '#7dd3fc' : '#94a3b8',
      borderRadius: 6, padding: '7px 16px', cursor: 'pointer', fontSize: 13, fontWeight: tab === key ? 700 : 400,
    }}>
      {label}
    </button>
  );

  return (
    <div style={{ padding: '24px 28px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        marginBottom: 20 }}>
        <div>
          <h2 style={{ color: '#f1f5f9', margin: 0, fontSize: 22, fontWeight: 700 }}>
            🗺️ Land Values
          </h2>
          <p style={{ color: '#64748b', fontSize: 13, margin: '4px 0 0' }}>
            Community-defined geographic values — cultural, ecological, spiritual, and governance areas.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {isManager && (
            <button onClick={() => setShowManager(!showManager)} style={{
              background: showManager ? '#1e3a5f' : '#1e293b',
              border: `1px solid ${showManager ? '#0ea5e9' : '#334155'}`,
              color: showManager ? '#7dd3fc' : '#94a3b8',
              borderRadius: 7, padding: '7px 14px', cursor: 'pointer', fontSize: 13,
            }}>
              {showManager ? '✓ Manager Mode' : 'Manager Mode'}
            </button>
          )}
          <button onClick={() => setTab('submit')} style={{
            background: '#16a34a', border: 'none', color: '#fff',
            borderRadius: 8, padding: '8px 14px', cursor: 'pointer',
            fontWeight: 700, fontSize: 13,
          }}>
            + Add Value
          </button>
        </div>
      </div>

      <StatsBar stats={stats} />

      {/* Category filter */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <button onClick={() => setCatFilter('')} style={{
          background: !catFilter ? '#1e3a5f' : '#1e293b',
          border: `1px solid ${!catFilter ? '#0ea5e9' : '#334155'}`,
          color: !catFilter ? '#7dd3fc' : '#94a3b8',
          borderRadius: 20, padding: '4px 14px', cursor: 'pointer', fontSize: 12,
        }}>
          All
        </button>
        {CATEGORIES.map(c => (
          <button key={c.value} onClick={() => setCatFilter(c.value)} style={{
            background: catFilter === c.value ? `${CATEGORY_COLOR[c.value]}33` : '#1e293b',
            border: `1px solid ${catFilter === c.value ? CATEGORY_COLOR[c.value] : '#334155'}`,
            color: catFilter === c.value ? CATEGORY_COLOR[c.value] : '#64748b',
            borderRadius: 20, padding: '4px 14px', cursor: 'pointer', fontSize: 12,
          }}>
            {CATEGORY_ICON[c.value]} {CATEGORY_LABEL[c.value]}
          </button>
        ))}
      </div>

      {/* Tab bar */}
      {tab !== 'submit' && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          {tabBtn('map',  '🗺️ Map')}
          {tabBtn('list', '📋 List')}
        </div>
      )}

      {/* Submit tab */}
      {tab === 'submit' && (
        <div style={{ maxWidth: 580, background: '#1e293b', borderRadius: 12,
          padding: 24, borderTop: '4px solid #16a34a' }}>
          <h3 style={{ color: '#f1f5f9', margin: '0 0 16px', fontSize: 18 }}>
            Add Land Value
          </h3>
          <LandValueForm
            onSubmit={handleSubmit}
            onCancel={() => setTab('map')}
          />
        </div>
      )}

      {/* Map tab */}
      {tab === 'map' && (
        loading
          ? <div style={{ color: '#64748b' }}>Loading map…</div>
          : <LandValueMap
              values={values}
              onValueClick={(v) => { setSelectedId(v._id); setTab('list'); }}
              height="520px"
            />
      )}

      {/* List tab */}
      {tab === 'list' && (
        loading
          ? <div style={{ color: '#64748b' }}>Loading values…</div>
          : values.length === 0
            ? (
              <div style={{ textAlign: 'center', color: '#64748b', padding: '40px 20px',
                border: '1px dashed #334155', borderRadius: 10 }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>🗺️</div>
                <div style={{ fontWeight: 600 }}>No land values submitted yet</div>
                <div style={{ fontSize: 13, marginTop: 4 }}>
                  Community members can add cultural, ecological, and governance values to the map.
                </div>
              </div>
            )
            : values.map(v => (
              <ValueCard
                key={v._id}
                value={v}
                isManager={isManager && showManager}
                isSelected={selectedId === v._id}
                onClick={() => setSelectedId(id => id === v._id ? null : v._id)}
                onVerify={handleVerify}
              />
            ))
      )}
    </div>
  );
}
